import type { AddinOrganisation } from './addin-organisation.model';

function asRecord(value: unknown): Record<string, unknown> | null {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) {
        return null;
    }
    return value as Record<string, unknown>;
}

function readString(row: Record<string, unknown>, ...keys: string[]): string {
    for (const key of keys) {
        const value = row[key];
        if (typeof value === 'string' && value.trim()) {
            return value.trim();
        }
    }
    return '';
}

function readId(row: Record<string, unknown>): number {
    const value = row['id'];
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function parseOrganisation(value: unknown): AddinOrganisation | null {
    const row = asRecord(value);
    if (!row) {
        return null;
    }
    const organisationCode = readString(row, 'organisationCode');
    if (!organisationCode) {
        return null;
    }
    return {
        id: readId(row),
        name: readString(row, 'name') || organisationCode,
        organisationCode,
        partnerId: readString(row, 'partnerId'),
    };
}

function readOrganisationRows(data: unknown): unknown[] {
    if (Array.isArray(data)) {
        return data;
    }
    const record = asRecord(data);
    if (!record) {
        return [];
    }
    const records = record['records'] ?? record['Records'];
    return Array.isArray(records) ? records : [];
}

/** Normalises a Get-ManagedOrganisations `data` payload into organisations with a code. */
export function parseManagedOrganisations(data: unknown): AddinOrganisation[] {
    const seen = new Set<string>();
    const organisations: AddinOrganisation[] = [];
    for (const row of readOrganisationRows(data)) {
        const organisation = parseOrganisation(row);
        if (!organisation || seen.has(organisation.organisationCode)) {
            continue;
        }
        seen.add(organisation.organisationCode);
        organisations.push(organisation);
    }
    return organisations;
}
